import { gql, useMutation } from '@apollo/client';
import { useCurrentEntityId } from '../../../hooks/useSelectedEntity/useSelectedEntity';
import { addClientOwner as ADD_CLIENT_OWNER } from '@admiin-com/ds-graphql';
import { Entity, EntityUser } from '@admiin-com/ds-graphql';

interface AddClientOwnerInput {
  entityId: string;
  email: string;
  firstName: string;
  lastName: string;
  mobile?: string;
  firmId: string;
}

interface AddClientOwnerResult {
  success: boolean;
  entityId: string;
  userId: string;
  ownerEntityUserId: string;
  clientGroupCode?: string;
  clientCode?: string;
  entity?: Entity;
  entityUser?: EntityUser;
}

export function useAddClientOwner() {
  const firmId = useCurrentEntityId();

  const [addClientOwnerMutation] = useMutation<
    { addClientOwner: AddClientOwnerResult },
    { input: AddClientOwnerInput }
  >(gql(ADD_CLIENT_OWNER), {
    refetchQueries: [
      // Refetch entity users to show the new owner in Client Users list
      'ListEntityUsers',
      'GetEntityUsers',
    ],
    awaitRefetchQueries: true,
  });

  const addClientOwner = async (input: Omit<AddClientOwnerInput, 'firmId'>) => {
    if (!firmId) {
      throw new Error('Firm ID is required');
    }

    if (!input.entityId) {
      throw new Error('Entity ID is required');
    }

    const result = await addClientOwnerMutation({
      variables: {
        input: {
          ...input,
          firmId,
        },
      },
      update: (cache, { data }) => {
        if (data?.addClientOwner?.success) {
          const { entityId, entity, entityUser } = data.addClientOwner;

          // Update entity cache with full entity object from response
          if (entity) {
            cache.modify({
              id: cache.identify({ __typename: 'Entity', id: entityId }),
              fields: {
                owner() {
                  return entity.owner;
                },
                contact() {
                  return entity.contact;
                },
                updatedAt() {
                  return entity.updatedAt;
                },
              },
            });
          }

          // Add new entityUser to the cache for listEntityUsers
          if (entityUser) {
            cache.modify({
              id: 'ROOT_QUERY',
              fields: {
                listEntityUsers(existingRefs = { items: [] }, { readField }) {
                  const newEntityUserRef = cache.writeFragment({
                    data: entityUser,
                    fragment: gql`
                      fragment NewEntityUser on EntityUser {
                        id
                        entityId
                        userId
                        firmId
                        email
                        invitedEmail
                        firstName
                        lastName
                        role
                        status
                        clientGroupCode
                        clientCode
                        createdAt
                        updatedAt
                        __typename
                      }
                    `,
                  });

                  // Check if already exists
                  const exists = existingRefs.items?.some(
                    (ref: any) => readField('id', ref) === entityUser.id
                  );

                  if (exists) {
                    return existingRefs;
                  }

                  return {
                    ...existingRefs,
                    items: [...(existingRefs.items || []), newEntityUserRef],
                  };
                },
              },
            });
          }

          cache.gc();
        }
      },
    });

    return result.data?.addClientOwner;
  };

  return { addClientOwner };
}
